import { SdkContext } from '../internal'
import { fetchExport, type DownloadedFile, type QueuedExport } from '../fileDownload'
import type {
  Feedback,
  FeedbackAboutRound,
  FeedbackBox,
  FeedbackDashboard,
  FeedbackPage,
  FeedbackPerson,
  FeedbackPreview,
  FeedbackRequest,
  FeedbackRequestStatus,
  FeedbackSection,
  FeedbackSettings,
  FeedbackTheme
} from '@/types/feedback'

const toPage = <T,>(result: unknown): FeedbackPage<T> => {
  if (Array.isArray(result)) return { items: result as T[], pagination: null }
  const r = result as { items?: T[]; data?: T[]; pagination?: FeedbackPage<T>['pagination'] } | null
  return { items: r?.items || r?.data || [], pagination: r?.pagination ?? null }
}

const arrayOf = <T,>(result: unknown): T[] => {
  if (Array.isArray(result)) return result as T[]
  const r = result as { items?: T[]; data?: T[] } | null
  return r?.items || r?.data || []
}

/**
 * F-49 Feedbacks. The community comes from X-CommunityId (never the path);
 * a community may have several Feedbacks spaces, so the routes that act in one
 * take `spaceId` (422 `spaceRequired` without it when there are several);
 * the server decides who reads (author and recipient only, 404 for anyone
 * else) and who manages. Paths written by hand (F-03).
 */
export class FeedbacksResource {
  constructor(private ctx: SdkContext) {}

  /** "Recebidos" (default) or "Enviados", newest first. `about`: true only about another person, false only direct. */
  async list(params: { box?: FeedbackBox; page?: number; limit?: number; spaceId?: string; about?: boolean } = {}): Promise<FeedbackPage<Feedback>> {
    return toPage<Feedback>(await this.ctx.raw({ httpMethod: 'GET', path: '/feedbacks' }, { query: params }))
  }

  async get(id: string): Promise<Feedback> {
    return this.ctx.call({ httpMethod: 'GET', path: '/feedbacks/{id}' }, { id })
  }

  /** The response carries `emailNotice` (the toast). */
  /** Free text in `body`, or the guided `sections` (never both: 422 `bodyAndSections`). */
  /** About a person (extension 2): `subjectId` instead of `recipientId`; it goes to their direct leader (none: 422 `subjectNoLeader`). */
  async give(body: {
    recipientId?: string
    subjectId?: string
    body?: string
    sections?: FeedbackSection[]
    messageToSubject?: string | null
    spaceId?: string
  }): Promise<Feedback> {
    return this.ctx.call({ httpMethod: 'POST', path: '/feedbacks', hasBody: true }, { body })
  }

  /** The author only. */
  async remove(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/feedbacks/{id}' }, { id })
  }

  /** The recipient, once; returns the card again. */
  async thank(id: string): Promise<Feedback> {
    return this.ctx.call({ httpMethod: 'POST', path: '/feedbacks/{id}/thank', hasBody: true }, { id, body: {} })
  }

  /** Who can receive or be asked: people who open the space, never the caller. */
  async people(search?: string, spaceId?: string): Promise<FeedbackPerson[]> {
    return arrayOf<FeedbackPerson>(
      await this.ctx.call({ httpMethod: 'GET', path: '/feedbacks/people' }, { query: { search: search?.trim() || undefined, spaceId } })
    )
  }

  /** "Pedidos": asked of me (`received`, default) or by me (`sent`). */
  async listRequests(params: { box?: FeedbackBox; status?: FeedbackRequestStatus; page?: number; limit?: number; spaceId?: string; about?: boolean } = {}): Promise<FeedbackPage<FeedbackRequest>> {
    return toPage<FeedbackRequest>(await this.ctx.raw({ httpMethod: 'GET', path: '/feedbacks/requests' }, { query: params }))
  }

  async getRequest(id: string): Promise<FeedbackRequest> {
    return this.ctx.call({ httpMethod: 'GET', path: '/feedbacks/requests/{id}' }, { id })
  }

  /** One pending request per pair per space (409). The response carries `emailNotice`. */
  async request(body: { giverId: string; message?: string | null; themeIds?: string[]; behaviorIds?: string[]; spaceId?: string }): Promise<FeedbackRequest> {
    return this.ctx.call({ httpMethod: 'POST', path: '/feedbacks/requests', hasBody: true }, { body })
  }

  /**
   * About another person (extension 2): the subject's direct leader or an
   * owner/admin asks up to `maxNominees` colleagues at once, one request each
   * (all or nothing: 403 `aboutDenied`, 422 per `giverIds[i]`, 409 `aboutPending`).
   */
  async requestAbout(body: {
    subjectId: string
    giverIds: string[]
    message?: string | null
    themeIds?: string[]
    behaviorIds?: string[]
    spaceId?: string
  }): Promise<FeedbackAboutRound> {
    return this.ctx.call({ httpMethod: 'POST', path: '/feedbacks/requests', hasBody: true }, { body })
  }

  /** The asked person answers: returns the new feedback, with `emailNotice`. `messageToSubject` only on a request about a person. */
  async answer(id: string, answer: { body?: string; sections?: FeedbackSection[]; messageToSubject?: string | null }): Promise<Feedback> {
    return this.ctx.call({ httpMethod: 'POST', path: '/feedbacks/requests/{id}/answer', hasBody: true }, { id, body: answer })
  }

  /** The text the API would compose from `sections` (the live preview); nothing is saved. */
  async preview(body: { sections: FeedbackSection[]; spaceId?: string; requestId?: string }): Promise<FeedbackPreview> {
    return this.ctx.call({ httpMethod: 'POST', path: '/feedbacks/preview', hasBody: true }, { body })
  }

  /** The space's settings: the nominee limit (anyone who opens the space; `canEdit` for owner/admin). */
  async getSettings(spaceId?: string): Promise<FeedbackSettings> {
    return this.ctx.call({ httpMethod: 'GET', path: '/feedbacks/settings' }, { query: { spaceId } })
  }

  /** Owner/admin: 1 to 20, `null` back to the default (5). */
  async updateSettings(body: { maxNominees: number | null; spaceId?: string }): Promise<FeedbackSettings> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/feedbacks/settings', hasBody: true }, { body })
  }

  // ---------- themes and behaviors (the community's catalog, F-49 extension) ----------

  /** Active themes for anyone who opens the space; owner/admin also the inactive ones with `includeInactive`. */
  async listThemes(params: { includeInactive?: boolean; spaceId?: string } = {}): Promise<FeedbackTheme[]> {
    return arrayOf<FeedbackTheme>(await this.ctx.call({ httpMethod: 'GET', path: '/feedbacks/themes' }, { query: params }))
  }

  /** Owner/admin from here on; each write returns the theme (inactive behaviors included). */
  async createTheme(body: { name: string; description?: string | null }): Promise<FeedbackTheme> {
    return this.ctx.call({ httpMethod: 'POST', path: '/feedbacks/themes', hasBody: true }, { body })
  }

  async updateTheme(themeId: string, body: { name?: string; description?: string | null; active?: boolean }): Promise<FeedbackTheme> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/feedbacks/themes/{themeId}', hasBody: true }, { themeId, body })
  }

  /** Every theme of the community (active and inactive), each once. */
  async reorderThemes(ids: string[]): Promise<FeedbackTheme[]> {
    return arrayOf<FeedbackTheme>(await this.ctx.call({ httpMethod: 'PUT', path: '/feedbacks/themes/order', hasBody: true }, { body: { ids } }))
  }

  async createBehavior(themeId: string, name: string): Promise<FeedbackTheme> {
    return this.ctx.call({ httpMethod: 'POST', path: '/feedbacks/themes/{themeId}/behaviors', hasBody: true }, { themeId, body: { name } })
  }

  async updateBehavior(themeId: string, behaviorId: string, body: { name?: string; active?: boolean }): Promise<FeedbackTheme> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/feedbacks/themes/{themeId}/behaviors/{behaviorId}', hasBody: true }, { themeId, behaviorId, body })
  }

  /** Every behavior of the theme (active and inactive), each once. */
  async reorderBehaviors(themeId: string, ids: string[]): Promise<FeedbackTheme> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/feedbacks/themes/{themeId}/behaviors/order', hasBody: true }, { themeId, body: { ids } })
  }

  async decline(id: string): Promise<FeedbackRequest> {
    return this.ctx.call({ httpMethod: 'POST', path: '/feedbacks/requests/{id}/decline', hasBody: true }, { id, body: {} })
  }

  async cancel(id: string): Promise<FeedbackRequest> {
    return this.ctx.call({ httpMethod: 'POST', path: '/feedbacks/requests/{id}/cancel', hasBody: true }, { id, body: {} })
  }

  /** Owner/admin; days in the community's time zone. 403 for anyone else. */
  async dashboard(params: { from?: string; to?: string; groupId?: string; spaceId?: string } = {}): Promise<FeedbackDashboard> {
    return this.ctx.call({ httpMethod: 'GET', path: '/feedbacks/dashboard' }, { query: params })
  }

  /** F-106 1: the same numbers (same filters) as a CSV or Excel file. Owner/admin; 403 for anyone else. Above 2,000 rows the file is e-mailed (202, `QueuedExport`). */
  async exportDashboard(params: { format: 'csv' | 'xlsx'; from?: string; to?: string; groupId?: string; spaceId?: string }): Promise<DownloadedFile | QueuedExport> {
    const accept = params.format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    const name = `feedbacks-${params.from || ''}_${params.to || ''}.${params.format}`
    return fetchExport(this.ctx, '/feedbacks/dashboard/export', params, accept, name)
  }
}
